/**
 * LYRIAH motion: GSAP 3.15 + ScrollTrigger + SplitText, with Lenis synced to
 * ScrollTrigger. Loaded only when "Animated effects" is on.
 * Rules (ui-ux-pro-max + GSAP skills): at most two pinned sections (hero and
 * theatre), scrub 0.6–1, transforms and opacity only, refresh after images
 * load. Nothing runs under prefers-reduced-motion: the page is complete and
 * readable without this file.
 */
(() => {
  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);
  if (window.SplitText) gsap.registerPlugin(window.SplitText);

  // Smooth scroll on desktop pointers, driven by GSAP's ticker so pins stay in sync.
  if (window.Lenis && window.theme?.smoothScroll && window.matchMedia('(pointer: fine)').matches) {
    const lenis = (window.lenis = new window.Lenis({ lerp: 0.09 }));
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(time => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  /* Desktop hero: a calm entrance; the photo keeps its size */
  const deskHero = document.querySelector('[data-hero]');
  if (deskHero && window.matchMedia('(min-width: 861px)').matches) {
    gsap.timeline()
      .from(deskHero.querySelector('[data-hero-window]'), { opacity: 0, y: 30, duration: 1.6, ease: 'expo.out' })
      .from(deskHero.querySelectorAll('.hero__side--start > div > *'), { opacity: 0, y: 18, duration: 1, stagger: 0.1, ease: 'power3.out' }, '<0.2')
      .from(deskHero.querySelector('.hero__side--end'), { opacity: 0, y: 14, duration: 0.9, ease: 'power3.out' }, '-=0.6');
  }

  /* Hero: intro, then the arch window opens to full screen (pin 1) */
  const hero = document.querySelector('[data-hero]');
  // Phones only: on desktop the hero is a static split and the photo keeps its size.
  if (hero && window.matchMedia('(max-width: 860px)').matches) {
    gsap.timeline()
      .from(hero.querySelector('.hero__word span'), { yPercent: 24, opacity: 0, duration: 2, ease: 'expo.out' })
      .from(hero.querySelector('[data-hero-window] img'), { opacity: 0, yPercent: 4, duration: 1.8, ease: 'expo.out' }, '<0.25')
      .from(hero.querySelectorAll('.hero__side > div'), { opacity: 0, y: 14, duration: 0.8, stagger: 0.12, ease: 'power2.out' }, '-=0.8');

    gsap.timeline({ scrollTrigger: { trigger: hero, start: 'top top', end: '+=110%', scrub: 1, pin: true } })
      .to(hero.querySelector('[data-hero-window]'), { '--t': '0vh', '--s': '0vw', '--b': '0vh', '--r': '0px', ease: 'none' })
      .to(hero.querySelector('[data-hero-window] img'), { scale: 1, ease: 'none' }, '<')
      .to(hero.querySelector('.hero__word'), { yPercent: -60, opacity: 0, ease: 'none' }, '<')
      .to(hero.querySelectorAll('.hero__side'), { opacity: 0, ease: 'none', duration: 0.3 }, '<')
      .to(hero.querySelector('[data-hero-overlay]'), { opacity: 1, ease: 'none', duration: 0.4 }, '-=0.4');
  }

  /* Manifesto: the words appear once, quickly, and then stay at full contrast
     (a scroll-linked fade left half-faded, unreadable text on screen). The split copy is aria-hidden. */
  document.querySelectorAll('[data-manifesto]').forEach(el => {
    if (!window.SplitText) return;
    const split = new window.SplitText(el, { type: 'words', aria: 'none' });
    gsap.from(split.words, {
      opacity: 0, y: 8, duration: 0.5, stagger: 0.04, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 80%', once: true }
    });
  });

  /* Theatre: seven programs while pinned (pin 2). Screen readers get a static copy. */
  document.querySelectorAll('[data-theatre]').forEach(theatre => {
    const list = theatre.querySelector('[data-theatre-programs]');
    const programs = [...list.children];
    const num = theatre.querySelector('[data-theatre-num]');
    const dots = theatre.querySelectorAll('.theatre__dots i');
    const colors = programs.map(p => p.style.getPropertyValue('--pc'));

    const copy = list.cloneNode(true);
    copy.className = 'visually-hidden';
    copy.removeAttribute('data-theatre-programs');
    list.before(copy);
    list.setAttribute('aria-hidden', 'true');
    theatre.classList.add('is-animated');

    const set = k => {
      theatre.style.setProperty('--c', colors[k]);
      num.firstChild.nodeValue = String(k + 1).padStart(2, '0');
      programs.forEach((p, i) => p.classList.toggle('is-on', i === k));
      theatre.querySelectorAll('.color-photos__img').forEach((img, i) => img.classList.toggle('is-on', i === k));
    };

    gsap.timeline({
      scrollTrigger: {
        trigger: theatre, start: 'top top', end: `+=${programs.length * 55}%`, scrub: 0.6, pin: true,
        onUpdate: self => set(Math.min(programs.length - 1, Math.floor(self.progress * programs.length)))
      }
    }).to(dots, { scaleX: 1, stagger: 1, duration: 1, ease: 'none' });
  });

  /* Thin gold reading-progress line under the header (decorative) */
  const progress = document.createElement('div');
  progress.className = 'scroll-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.append(progress);
  gsap.to(progress, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });

  /* Banner: the photo settles from a slow zoom and drifts on scroll; the text rises line by line */
  const banner = document.querySelector('.banner');
  if (banner) {
    const photo = banner.querySelector('.banner__media img');
    const content = banner.querySelector('.banner__content');
    const title = banner.querySelector('[data-split-lines]');
    const intro = gsap.timeline({ defaults: { ease: 'expo.out' } });
    if (photo) intro.from(photo, { scale: 1.18, duration: 2.6, ease: 'power3.out' });
    intro.from(banner.querySelector('.banner__brand'), { opacity: 0, letterSpacing: '1.2em', duration: 1.8 }, 0.2);
    if (title && window.SplitText) {
      const lines = new window.SplitText(title, { type: 'lines', mask: 'lines', aria: 'none' });
      intro.from(lines.lines, { yPercent: 110, duration: 1.5, stagger: 0.14 }, 0.35);
    }
    intro.from(banner.querySelectorAll('.banner__text, .banner .button'), { opacity: 0, y: 22, duration: 1.2, stagger: 0.1 }, 0.8);

    if (photo) {
      gsap.to(photo, { yPercent: 12, ease: 'none', scrollTrigger: { trigger: banner, start: 'top top', end: 'bottom top', scrub: true } });
    }
    gsap.to(content, { yPercent: -18, opacity: 0, ease: 'none', scrollTrigger: { trigger: banner, start: 'center center', end: 'bottom top', scrub: true } });
  }

  /* Section titles rise line by line as they enter */
  document.querySelectorAll('.showcase__head h2, .brand-story h2, .story__text h2, .glow h2').forEach(heading => {
    if (!window.SplitText) return;
    const lines = new window.SplitText(heading, { type: 'lines', mask: 'lines', aria: 'none' });
    gsap.from(lines.lines, { yPercent: 110, duration: 1.4, stagger: 0.12, ease: 'expo.out', scrollTrigger: { trigger: heading, start: 'top 85%' } });
  });

  /* Product cards: a short, quiet fade so the products are there almost at once */
  ScrollTrigger.batch('.devices__item, .mosaic__item', {
    start: 'top 92%', once: true,
    onEnter: items => gsap.from(items, { opacity: 0, y: 10, duration: 0.5, stagger: 0.05, ease: 'power2.out', clearProps: 'transform' })
  });

  /* Brand story: the photo unveils from the top down and drifts slowly */
  document.querySelectorAll('.brand-story__media').forEach(media => {
    const img = media.querySelector('img');
    gsap.from(media, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.6, ease: 'expo.inOut', clearProps: 'clipPath', scrollTrigger: { trigger: media, start: 'top 80%' } });
    if (img) gsap.fromTo(img, { yPercent: -6, scale: 1.12 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: media, start: 'top bottom', end: 'bottom top', scrub: true } });
  });

  /* LED colors: scrolling through the section walks the glow through the seven programs */
  document.querySelectorAll('[data-glow]').forEach(glow => {
    const count = glow.querySelectorAll('.glow__program').length;
    let last = 0;
    ScrollTrigger.create({
      trigger: glow, start: 'top 55%', end: 'bottom 45%',
      onUpdate: self => {
        const k = Math.min(count - 1, Math.floor(self.progress * count));
        const picked = Date.now() - Number(glow.dataset.pickedAt || 0) < 1500;
        if (k !== last && glow.setProgram && !picked) glow.setProgram(k);
        last = k;
      }
    });

    // Entrance: the photo simply fades in, then the color circles appear one by one
    // (the whole frame fades, not one image: with seven color photos stacked,
    // fading only the first left the red one stuck at opacity 0)
    const visual = glow.querySelector('.glow__visual');
    gsap.timeline({ scrollTrigger: { trigger: glow, start: 'top 72%' } })
      .from(visual, { opacity: 0, duration: 1.4, ease: 'power2.out', clearProps: 'opacity' })
      .from(glow.querySelectorAll('.glow__chip'), { scale: 0, opacity: 0, duration: 0.6, stagger: 0.07, ease: 'back.out(2)' }, '-=0.6');
  });

  /* Service strip: each fact rises in turn */
  ScrollTrigger.batch('.promises__list li', {
    start: 'top 92%', once: true,
    onEnter: els => gsap.from(els, { opacity: 0, y: 26, duration: 1.1, stagger: 0.15, ease: 'power3.out' })
  });

  /* Gentle reveals */
  ScrollTrigger.batch('.ritual__step, .area, .trust__facts li, [data-reveal], .brand-story__text > :not(h2), .showcase__head p, .showcase__all', {
    start: 'top 90%', once: true,
    onEnter: els => gsap.from(els, { opacity: 0, y: 12, duration: 0.6, stagger: 0.06, ease: 'power2.out' })
  });

  const finale = document.querySelector('[data-finale-title]');
  if (finale && window.SplitText) {
    const lines = new window.SplitText(finale, { type: 'lines', mask: 'lines', aria: 'none' });
    gsap.from(lines.lines, { yPercent: 100, duration: 1.6, stagger: 0.16, ease: 'expo.out', scrollTrigger: { trigger: finale, start: 'top 80%' } });
  }

  window.addEventListener('load', () => ScrollTrigger.refresh());
})();
